import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import { API_BASE_URL } from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";
import logo from "../assets/image/eblackdog_sentinal.png";

const initialRouteForm = {
  route_id: "",
  r_name: "",
  r_from: "",
  r_to: "",
  r_from_lat: "",
  r_from_lng: "",
  r_to_lat: "",
  r_to_lng: "",
};

const initialCheckpoint = {
  checkpoints_id: "",
  checkpoint_name: "",
  lat: "",
  lng: "",
};

function valueOrEmpty(value) {
  return value === null || value === undefined ? "" : String(value);
}

function createCustomIcon({
  label,
  bg = "#2563eb",
  border = "#ffffff",
  textColor = "#ffffff",
  isLogo = false,
  logoUrl = "",
}) {
  if (isLogo && logoUrl) {
    return L.divIcon({
      className: "custom-map-pin-wrapper",
      html: `
        <div style="
          width:40px;
          height:40px;
          border-radius:50%;
          background:${bg};
          border:3px solid ${border};
          box-shadow:0 4px 14px rgba(0,0,0,0.3);
          display:flex;
          align-items:center;
          justify-content:center;
          overflow:hidden;
        ">
          <img src="${logoUrl}" alt="logo" style="width:28px;height:28px;object-fit:contain;" />
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -32],
    });
  }

  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        width:30px;
        height:30px;
        border-radius:50% 50% 50% 0;
        transform:rotate(-45deg);
        background:${bg};
        border:2px solid ${border};
        box-shadow:0 4px 10px rgba(0,0,0,0.25);
        display:flex;
        align-items:center;
        justify-content:center;
      ">
        <span style="
          transform:rotate(45deg);
          font-size:12px;
          font-weight:700;
          color:${textColor};
          font-family:Arial,sans-serif;
        ">
          ${label}
        </span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const fromIcon = createCustomIcon({
  label: "F",
  bg: "#16a34a",
  isLogo: true,
  logoUrl: logo,
});

const toIcon = createCustomIcon({
  label: "T",
  bg: "#dc2626",
  isLogo: true,
  logoUrl: logo,
});

function checkpointIcon(index) {
  return createCustomIcon({
    label: String(index + 1),
    bg: "#2563eb",
    isLogo: true,
    logoUrl: logo,
  });
}

export default function RouteMaster() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editId = params.get("edit_id") || "";
  const isEdit = Boolean(editId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [form, setForm] = useState(initialRouteForm);
  const [checkpoints, setCheckpoints] = useState([{ ...initialCheckpoint }]);
  const [activeTarget, setActiveTarget] = useState(null);

  const activeTargetRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);
  const fromMarkerRef = useRef(null);
  const toMarkerRef = useRef(null);
  const checkpointMarkersRef = useRef([]);
  const hasAutoFitRef = useRef(false);

  useEffect(() => {
    activeTargetRef.current = activeTarget;
  }, [activeTarget]);

  const setLatLngOnActiveTarget = useCallback((lat, lng, shouldCenter = false, label = "") => {
    const target = activeTargetRef.current;

    if (!target) {
      toast.error("Please click Pick on Map for From, To, or Checkpoint first.");
      return;
    }

    const lat7 = Number(lat).toFixed(7);
    const lng7 = Number(lng).toFixed(7);

    if (target === "from") {
      setForm((prev) => ({
        ...prev,
        r_from_lat: lat7,
        r_from_lng: lng7,
        r_from: label || prev.r_from,
      }));
    } else if (target === "to") {
      setForm((prev) => ({
        ...prev,
        r_to_lat: lat7,
        r_to_lng: lng7,
        r_to: label || prev.r_to,
      }));
    } else if (typeof target === "object" && target.type === "checkpoint") {
      setCheckpoints((prev) =>
        prev.map((checkpoint, index) =>
          index === target.index
            ? {
                ...checkpoint,
                lat: lat7,
                lng: lng7,
                checkpoint_name: label || checkpoint.checkpoint_name,
              }
            : checkpoint
        )
      );
    }

    // Guard: only pan/center if the map instance is still alive.
    if (shouldCenter && mapInstanceRef.current) {
      mapInstanceRef.current.setView([lat, lng], 16);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate, editId]);

  // ---------------------------------------------------------------------
  // Map initialization
  //
  // FIX: previously this effect fired three untracked `setTimeout` calls
  // (`map.invalidateSize()` at 100ms/500ms/1000ms) without ever storing
  // their IDs. If the component unmounted before those timers fired
  // (e.g. quick navigation, or React StrictMode's mount->unmount->mount
  // cycle in dev), the cleanup below would call `map.remove()`, and then
  // the orphaned timeout would still fire afterwards and call
  // `invalidateSize()` on an already-removed map instance. Leaflet then
  // tries to read `_leaflet_pos` off a DOM node whose pane was already
  // torn down, throwing:
  //   "Cannot read properties of undefined (reading '_leaflet_pos')"
  //
  // Fix: track every timeout ID and clear them all in the cleanup
  // function, and wrap every invalidateSize call in a guard that checks
  // the map is still the current, loaded instance before touching it.
  // ---------------------------------------------------------------------
useEffect(() => {
  if (loading) return;
  if (!mapRef.current) return;

  if (mapInstanceRef.current) {
    mapInstanceRef.current.invalidateSize();
    return;
  }

  const map = L.map(mapRef.current, {
    zoomControl: true,
    center: [20.5937, 78.9629],
    zoom: 5,
  });

  mapInstanceRef.current = map;

L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
  maxZoom: 20,
  subdomains: "abcd",
  attribution: "&copy; OpenStreetMap &copy; CARTO",
  crossOrigin: true,
}).addTo(map);

  map.on("click", (ev) => {
    setLatLngOnActiveTarget(ev.latlng.lat, ev.latlng.lng, true);
  });

  const resizeObserver = new ResizeObserver(() => {
    if (mapInstanceRef.current === map) {
      setTimeout(() => map.invalidateSize(), 100);
    }
  });

  resizeObserver.observe(mapRef.current);

  setTimeout(() => {
    if (mapInstanceRef.current === map) {
      map.invalidateSize();
    }
  }, 300);

  return () => {
    resizeObserver.disconnect();
    map.off();
    map.remove();

    if (mapInstanceRef.current === map) {
      mapInstanceRef.current = null;
    }
  };
}, [loading, setLatLngOnActiveTarget]);

  useEffect(() => {
    if (loading) return;
    if (!mapInstanceRef.current) return;
    syncMarkers(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, form, checkpoints]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);

      const requests = [
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ];

      if (editId) {
        requests.push(
          fetch(`${API_BASE_URL}/route/${editId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          })
        );
      }

      const [dashboardRes, routeRes] = await Promise.all(requests);

      if (!dashboardRes.ok) {
        throw new Error("Failed to fetch dashboard");
      }

      const dashboardResult = await dashboardRes.json();
      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      if (editId) {
        if (!routeRes?.ok) {
          throw new Error("Failed to fetch route details");
        }

        const routeResult = await routeRes.json();
        const route = routeResult?.data?.route || routeResult?.data || {};
        const routeCheckpoints = routeResult?.data?.checkpoints || route?.checkpoints || [];

        setForm({
          route_id: route.route_id || route.id || editId,
          r_name: route.r_name || "",
          r_from: route.r_from || "",
          r_to: route.r_to || "",
          r_from_lat: valueOrEmpty(route.r_from_lat),
          r_from_lng: valueOrEmpty(route.r_from_lng),
          r_to_lat: valueOrEmpty(route.r_to_lat),
          r_to_lng: valueOrEmpty(route.r_to_lng),
        });

        setCheckpoints(
          Array.isArray(routeCheckpoints) && routeCheckpoints.length > 0
            ? routeCheckpoints.map((item) => ({
                checkpoints_id: item.checkpoints_id || item.id || "",
                checkpoint_name: item.checkpoint_name || "",
                lat: valueOrEmpty(item.lat),
                lng: valueOrEmpty(item.lng),
              }))
            : [{ ...initialCheckpoint }]
        );
      }
    } catch (error) {
      console.error("Route master page error:", error);
      toast.error(error.message || "Failed to load page data.");
    } finally {
      setLoading(false);
    }
  };

  const triggerFenceSync = async (routeId, token) => {
    try {
      await fetch(`${API_BASE_URL}/route/${routeId}/fence-sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.warn("Fence sync failed:", error);
    }
  };

  const triggerQrSync = async (routeId, token) => {
    try {
      await fetch(`${API_BASE_URL}/route/${routeId}/qr-sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.warn("QR sync failed:", error);
    }
  };

  const syncMarkers = (forceFit = false) => {
    const map = mapInstanceRef.current;
    // Guard: bail if the map is gone or Leaflet has already torn it down.
    if (!map) return;

    const fLat = parseFloat(form.r_from_lat || "");
    const fLng = parseFloat(form.r_from_lng || "");

    if (!Number.isNaN(fLat) && !Number.isNaN(fLng)) {
      if (fromMarkerRef.current) {
        fromMarkerRef.current.setLatLng([fLat, fLng]);
        fromMarkerRef.current.setPopupContent(`From: ${form.r_from || "Selected location"}`);
      } else {
        fromMarkerRef.current = L.marker([fLat, fLng], { icon: fromIcon })
          .addTo(map)
          .bindPopup(`From: ${form.r_from || "Selected location"}`);
      }
    } else if (fromMarkerRef.current) {
      map.removeLayer(fromMarkerRef.current);
      fromMarkerRef.current = null;
    }

    const tLat = parseFloat(form.r_to_lat || "");
    const tLng = parseFloat(form.r_to_lng || "");

    if (!Number.isNaN(tLat) && !Number.isNaN(tLng)) {
      if (toMarkerRef.current) {
        toMarkerRef.current.setLatLng([tLat, tLng]);
        toMarkerRef.current.setPopupContent(`To: ${form.r_to || "Selected location"}`);
      } else {
        toMarkerRef.current = L.marker([tLat, tLng], { icon: toIcon })
          .addTo(map)
          .bindPopup(`To: ${form.r_to || "Selected location"}`);
      }
    } else if (toMarkerRef.current) {
      map.removeLayer(toMarkerRef.current);
      toMarkerRef.current = null;
    }

    checkpointMarkersRef.current.forEach((marker) => {
      if (marker) map.removeLayer(marker);
    });

    checkpointMarkersRef.current = checkpoints.map((checkpoint, index) => {
      const lat = parseFloat(checkpoint.lat || "");
      const lng = parseFloat(checkpoint.lng || "");

      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

      return L.marker([lat, lng], { icon: checkpointIcon(index) })
        .addTo(map)
        .bindPopup(`Checkpoint ${index + 1}: ${checkpoint.checkpoint_name || "Selected location"}`);
    });

    const layers = [
      fromMarkerRef.current,
      toMarkerRef.current,
      ...checkpointMarkersRef.current,
    ].filter(Boolean);

    if (layers.length > 0 && (forceFit || !hasAutoFitRef.current)) {
      const group = L.featureGroup(layers);
      map.fitBounds(group.getBounds().pad(0.25));
      hasAutoFitRef.current = true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckpointChange = (index, field, value) => {
    setCheckpoints((prev) =>
      prev.map((checkpoint, i) => (i === index ? { ...checkpoint, [field]: value } : checkpoint))
    );
  };

  const addCheckpoint = () => {
    setCheckpoints((prev) => [...prev, { ...initialCheckpoint }]);
    toast.success("New checkpoint added!");
  };

  const removeCheckpoint = (index) => {
    setCheckpoints((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ ...initialCheckpoint }];
    });

    toast("Checkpoint removed.", { icon: "ℹ️" });
  };

  const geocodeAddress = async (target, text) => {
    const searchText = String(text || "").trim();

    if (!searchText) {
      toast.error("Enter a location name first.");
      return;
    }

    try {
      setActiveTarget(target);
      activeTargetRef.current = target;

     const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(searchText)}&limit=1`;
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
      });

    const results = await res.json();

if (!results?.features?.length) {
  toast.error("Location not found. Please try a different search term.");
  return;
}

const place = results.features[0];
const [lng, lat] = place.geometry.coordinates;
const label =
  place.properties.name ||
  place.properties.city ||
  place.properties.state ||
  searchText;

      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        toast.error("Invalid location coordinates.");
        return;
      }

      setLatLngOnActiveTarget(lat, lng, true, place.display_name || searchText);
      toast.success("Location found and pinned!");
    } catch (error) {
      console.error("Geocode search error:", error);
      toast.error("Location search failed. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.r_name.trim()) {
      toast.error("Route Name is required.");
      return;
    }

    if (!form.r_from.trim()) {
      toast.error("From location name is required.");
      return;
    }

    if (!form.r_to.trim()) {
      toast.error("To location name is required.");
      return;
    }

    if (!form.r_from_lat || !form.r_from_lng) {
      toast.error("Please pick or search the From location on the map.");
      return;
    }

    if (!form.r_to_lat || !form.r_to_lng) {
      toast.error("Please pick or search the To location on the map.");
      return;
    }

    const validCheckpoints = checkpoints.filter((checkpoint) =>
      String(checkpoint.checkpoint_name || "").trim()
    );

    if (validCheckpoints.length === 0) {
      toast.error("Please add at least one checkpoint.");
      return;
    }

    for (let i = 0; i < validCheckpoints.length; i++) {
      const checkpoint = validCheckpoints[i];

      if (!checkpoint.lat || !checkpoint.lng) {
        toast.error(`Please pick or search location for Checkpoint ${i + 1}.`);
        return;
      }
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const payload = {
        r_name: form.r_name.trim(),
        r_from: form.r_from.trim(),
        r_to: form.r_to.trim(),
        r_from_lat: form.r_from_lat,
        r_from_lng: form.r_from_lng,
        r_to_lat: form.r_to_lat,
        r_to_lng: form.r_to_lng,
        checkpoints: validCheckpoints.map((checkpoint) => ({
          checkpoints_id: checkpoint.checkpoints_id || null,
          checkpoint_name: checkpoint.checkpoint_name.trim(),
          lat: checkpoint.lat,
          lng: checkpoint.lng,
        })),
      };

      const url = isEdit
        ? `${API_BASE_URL}/route/${form.route_id || editId}`
        : `${API_BASE_URL}/route`;

      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save route");
      }

      const savedRouteId = data?.data?.route_id || data?.data?.id || form.route_id || editId;

      if (savedRouteId) {
        await triggerFenceSync(savedRouteId, token);
        await triggerQrSync(savedRouteId, token);
      }

      toast.success(isEdit ? "Route updated successfully!" : "Route saved successfully!");

      if (!isEdit) {
        navigate("/route_list");
      }
    } catch (error) {
      console.error("Save route error:", error);
      toast.error(error.message || "Failed to save route.");
    } finally {
      setSaving(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const escortPage = useMemo(() => "/escort_master", []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-4 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)]">Loading route management...</p>
      </div>
    );
  }

  return (
    <Layout dashboardData={dashboardData} routePage={routePage} activePage="routes" activeMenu="routes">
      <Toaster position="top-right" />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold tracking-tight m-0">Route Management</h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          {isEdit ? "Edit an existing patrol route" : "Create a new patrol route"}
        </p>

        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold m-0">
                {isEdit ? "Edit Route" : "New Route"}
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Manage route name, start and end points, checkpoint coordinates, and QR codes
              </p>
            </div>

            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(escortPage)}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition hover:opacity-95 hover:-translate-y-0.5 bg-[var(--btn-bg)] text-[var(--btn-text)] shadow-[0_4px_18px_var(--accent-glow)]"
              >
                Escort Route
              </button>

              <button
                type="button"
                onClick={() => navigate("/route_list")}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition hover:opacity-90 hover:-translate-y-0.5"
              >
                Route List
              </button>
            </div>
          </div>
        </div>

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="mb-4">
            <h3 className="text-[18px] font-extrabold m-0">
              {isEdit ? "Edit Route" : "New Route"}
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Create patrol routes, assign checkpoints, generate QR codes, and pick coordinates directly from the map
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div>
              <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Route Name <span className="text-[var(--red)]">*</span>
              </label>
              <input
                type="text"
                name="r_name"
                value={form.r_name}
                onChange={handleChange}
                placeholder="Enter route name"
                required
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <LocationField
                label="From *"
                name="r_from"
                value={form.r_from}
                lat={form.r_from_lat}
                lng={form.r_from_lng}
                onChange={handleChange}
                onPick={() => {
                  setActiveTarget("from");
                  activeTargetRef.current = "from";
                  toast("Click on the map to set: From location", { icon: "📍" });
                }}
                onSearch={() => geocodeAddress("from", form.r_from)}
              />

              <LocationField
                label="To *"
                name="r_to"
                value={form.r_to}
                lat={form.r_to_lat}
                lng={form.r_to_lng}
                onChange={handleChange}
                onPick={() => {
                  setActiveTarget("to");
                  activeTargetRef.current = "to";
                  toast("Click on the map to set: To location", { icon: "📍" });
                }}
                onSearch={() => geocodeAddress("to", form.r_to)}
              />
            </div>

            <div className="mt-6">
              <div className="flex justify-between items-center gap-3 mb-4 flex-wrap">
                <div>
                  <h3 className="text-[16px] font-extrabold m-0">Checkpoint Details</h3>
                  <p className="mt-1 text-[12px] text-[var(--text-muted)]">
                    Add one or more checkpoints for this patrol route
                  </p>
                </div>

                <button
                  type="button"
                  onClick={addCheckpoint}
                  className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition hover:opacity-95 hover:-translate-y-0.5 bg-[var(--btn-bg)] text-[var(--btn-text)] shadow-[0_4px_18px_var(--accent-glow)]"
                >
                  + Add Checkpoint
                </button>
              </div>

              <div className="space-y-3.5">
                {checkpoints.map((checkpoint, index) => (
                  <div
                    key={`${checkpoint.checkpoints_id || "new"}-${index}`}
                    className="border border-[var(--surface-border)] rounded-2xl p-4 bg-[var(--surface)]"
                  >
                    <div className="flex justify-between items-center gap-3 mb-4 flex-wrap">
                      <div className="inline-flex px-3 py-1.5 rounded-full text-[11px] font-extrabold bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)]">
                        Checkpoint {index + 1}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeCheckpoint(index)}
                        className="border-0 rounded-xl px-3.5 py-1.5 text-[12px] font-extrabold cursor-pointer bg-[var(--red)] text-white hover:opacity-90"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                          Checkpoint Name <span className="text-[var(--red)]">*</span>
                        </label>
                        <input
                          type="text"
                          value={checkpoint.checkpoint_name}
                          onChange={(e) =>
                            handleCheckpointChange(index, "checkpoint_name", e.target.value)
                          }
                          placeholder="Checkpoint name"
                          required
                          className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--card-bg)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                        />
                      </div>

                      <ReadOnlyInput label="Latitude" value={checkpoint.lat} placeholder="Lat" />
                      <ReadOnlyInput label="Longitude" value={checkpoint.lng} placeholder="Lng" />
                    </div>

                    <div className="flex gap-3 flex-wrap mt-3">
                      <button
                        type="button"
                        onClick={() => {
                          const target = { type: "checkpoint", index };
                          setActiveTarget(target);
                          activeTargetRef.current = target;
                          toast(`Click on the map to set: Checkpoint ${index + 1}`, { icon: "📍" });
                        }}
                        className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer bg-[var(--btn-bg)] text-[var(--btn-text)] hover:opacity-90"
                      >
                        Pick on Map
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          geocodeAddress(
                            { type: "checkpoint", index },
                            checkpoint.checkpoint_name
                          )
                        }
                        className="border border-[var(--surface-border)] bg-[var(--card-bg)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                      >
                        Search Location
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="mt-5 w-full border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer bg-[var(--btn-bg)] text-[var(--btn-text)] shadow-[0_4px_18px_var(--accent-glow)] transition hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {saving
                ? "Saving..."
                : isEdit
                ? "Update Route & Generate QR"
                : "Save Route & Generate QR"}
            </button>
          </form>
        </div>

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold m-0">Route Map</h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Search, click, and confirm exact map coordinates for the route and checkpoints
            </p>
          </div>

          <div
            ref={mapRef}
            className="w-full h-[420px] rounded-2xl overflow-hidden border border-[var(--surface-border)] bg-[var(--surface)]"
          />
        </div>

        <style>{`
          .custom-map-pin-wrapper {
            background: transparent !important;
            border: none !important;
          }

          .leaflet-control-geocoder {
            border: 1px solid var(--surface-border) !important;
            background: var(--card-bg) !important;
            border-radius: 14px !important;
            overflow: hidden !important;
            box-shadow: 0 10px 30px rgba(0,0,0,0.35) !important;
          }

          .leaflet-control-geocoder-icon {
            width: 44px !important;
            height: 44px !important;
            background: var(--btn-bg) !important;
            border-radius: 14px !important;
            position: relative !important;
            background-image: none !important;
          }

          .leaflet-control-geocoder-icon::before {
            content: "";
            width: 18px;
            height: 18px;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M10 2a8 8 0 105.293 14.293l4.707 4.707 1.414-1.414-4.707-4.707A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z'/%3E%3C/svg%3E");
          }

          .leaflet-control-geocoder-expanded {
            width: 360px !important;
            display: flex !important;
            align-items: center !important;
            border-radius: 14px !important;
          }
          .leaflet-container {
  width: 100% !important;
  height: 100% !important;
  min-height: 420px !important;
  z-index: 1;
}

          .leaflet-control-geocoder-expanded .leaflet-control-geocoder-icon {
            border-radius: 14px 0 0 14px !important;
            flex: 0 0 44px !important;
          }

          .leaflet-control-geocoder-form {
            flex: 1 !important;
          }

          .leaflet-control-geocoder-form input {
            width: 100% !important;
            height: 44px !important;
            background: var(--card-bg) !important;
            color: var(--text-primary) !important;
            border: none !important;
            outline: none !important;
            padding: 0 14px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
          }

          .leaflet-control-geocoder-form input::placeholder {
            color: var(--text-faint) !important;
          }

          .leaflet-control-geocoder-alternatives {
            background: var(--card-bg) !important;
            border: 1px solid var(--surface-border) !important;
            border-radius: 12px !important;
            margin-top: 6px !important;
            box-shadow: 0 14px 35px rgba(0,0,0,0.4) !important;
            overflow: hidden !important;
          }

          .leaflet-control-geocoder-alternatives li {
            color: var(--text-primary) !important;
            padding: 10px 12px !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            border-bottom: 1px solid var(--surface-border) !important;
          }

          .leaflet-control-geocoder-alternatives li:hover {
            background: var(--accent-faint) !important;
          }

          .leaflet-control-zoom a {
            color: #111827 !important;
            font-weight: 800 !important;
          }
        `}</style>
      </div>
    </Layout>
  );
}

function LocationField({ label, name, value, lat, lng, onChange, onPick, onSearch }) {
  return (
    <div>
      <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
        {label}
      </label>

      <div className="space-y-3">
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSearch();
            }
          }}
          placeholder="Search or click on map"
          required
          className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
        />

        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={lat}
            readOnly
            placeholder="Lat"
            className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
          />
          <input
            type="text"
            value={lng}
            readOnly
            placeholder="Lng"
            className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
          />
        </div>

        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={onPick}
            className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer bg-[var(--btn-bg)] text-[var(--btn-text)] hover:opacity-90"
          >
            Pick on Map
          </button>

          <button
            type="button"
            onClick={onSearch}
            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
          >
            Search Location
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadOnlyInput({ label, value, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
        {label}
      </label>
      <input
        type="text"
        value={value}
        readOnly
        placeholder={placeholder}
        className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
      />
    </div>
  );
}